import { faCheck, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Banner } from 'component-library';
import { useSelector } from 'react-redux';
import { Package, PackageUpgrade, PriceListItem } from '../../../../models/ApiModels';
interface FunctionPackage {
  id: string;
  name: string;
  desc: string;
  customerPrice: number;
  retailPrice: number;
  features: string[];
}
interface Props {
  system: any;
  prices?: any;
  listPrices?: any;
  oemDetails?: any;
  setNewPackage: (id: string) => void;
  newPackage: string;
}
const FunctionPackagesNew = ({ system, prices, listPrices, newPackage, setNewPackage, oemDetails }: Props) => {
  const packages = useSelector((state: any) => state.configurations.packages);
  const upgrades: PackageUpgrade[] = useSelector((state: any) => state.configurations.upgrades);
  const currentPackage: Package = packages.find((pack: Package) => pack.id === system?.packageId);
  const isLegacy =
    currentPackage?.id === 'PKG-1-1' ||
    currentPackage?.id === 'PKG-2-1' ||
    currentPackage?.id === 'PKG-3-1' ||
    currentPackage?.id === 'PKG-4-1';

  function GetCustomerPackagePrice(pack: Package) {
    const upgrade = upgrades.find(
      (u: PackageUpgrade) =>
        u.upgradeBasePackageId === pack.id && u.id && currentPackage?.availableUpgrades?.includes(u.id)
    );

    if (upgrade && currentPackage?.availableUpgrades?.some((p: string) => p === upgrade.id)) {
      return prices?.priceList.find((p: PriceListItem) => p.internalId === upgrade.id);
    } else {
      return prices?.priceList.find((p: PriceListItem) => p.internalId === pack.id);
    }
  }

  function GetRetailPackagePrice(pack: Package) {
    const upgrade = upgrades.find(
      (u: PackageUpgrade) =>
        u.upgradeBasePackageId === pack.id && u.id && currentPackage?.availableUpgrades?.includes(u.id)
    );

    if (upgrade && currentPackage?.availableUpgrades?.some((p: string) => p === upgrade.id)) {
      return listPrices?.priceList.find((p: PriceListItem) => p.internalId === upgrade.id);
    } else {
      return listPrices?.priceList.find((p: PriceListItem) => p.internalId === pack.id);
    }
  }

  const functionPackages = [
    {
      id: 'PKG-5-1',
      name: 'Cruise',
      desc: 'Automatically compensating for every condition on the boat, like trim, wind, uneven load and turns in all speeds.',
      features: ['Auto trim', 'Auto list', 'Coordinated turn', 'Underwater lights'],
      complementaryFeatures: [],
    },
    {
      id: 'PKG-6-1',
      name: 'Ultimate',
      desc: 'The full Humphree experience with active stabilization, intelligence and our patented adaptive pitch control.',
      features: ['Pitch control', 'Roll control', 'Driver intention', 'True turn compensation'],
      complementaryFeatures: ['Auto trim', 'Auto list', 'Coordinated turn', 'Underwater lights'],
    },
  ];

  function handleCustomerDisplayPrice(pack: Package) {
    if (currentPackage?.id === pack.id) return null;

    const price = GetCustomerPackagePrice(pack);
    if (!price?.unitPrice) return null;

    const currency = price.currencyCode || oemDetails?.currencyCode || 'SEK';
    return `${price.unitPrice.toLocaleString()} ${currency}`;
  }

  function handleRetailDisplayPrice(pack: Package) {
    const customerPriceItem = GetCustomerPackagePrice(pack);
    const retailPriceItem = GetRetailPackagePrice(pack);
    const customerPrice = customerPriceItem?.unitPrice;
    const retailPrice = retailPriceItem?.unitPrice;

    // Don't show if current package
    if (currentPackage?.id === pack.id) return '';

    // Don't show if no price difference or missing retail price
    if (customerPrice === retailPrice || retailPrice === undefined) return '';

    // Show retail price if both exist and retail is higher
    if (customerPrice !== undefined && retailPrice > customerPrice) {
      const currency =
        retailPriceItem?.currencyCode && retailPriceItem.currencyCode !== '' ? retailPriceItem.currencyCode : 'SEK';
      return `(retail price ${retailPrice.toLocaleString()} ${currency})`;
    }

    return '';
  }

  return (
    <div className='flex flex-col gap-6'>
      {isLegacy && (
        <Banner
          icon={true}
          warning={false}
          text={
            'You have our old function package structure. Your upgrade to unlimited is based on the price difference with your current package ' +
            currentPackage?.displayName +
            '.'
          }
          color={'Super Light Blue'}
        />
      )}
      <div className='grid xl:grid-flow-col gap-4'>
        {functionPackages
          .filter(
            (functionPackage: any) =>
              currentPackage?.id === 'PKG-5-1' || !currentPackage || functionPackage.id === 'PKG-6-1'
          )
          .map((functionPackage: any) => {
            return (
              <div key={functionPackage.id} className='bg-gray-5 p-8 xl:min-h-[600px] flex flex-col'>
                <h2 className='prose-heading2'>{functionPackage.name}</h2>
                <p className='prose-paragraphBig border-b border-gray-60 pb-8 mb-8'>
                  {isLegacy
                    ? 'Upgrade your license to our new package series. Get the full Humphree experience with active stabilization, intelligence and our patented adaptive pitch control. Automatically compensating for every condition on the boat, like trim, wind, uneven load and turns in all speeds.'
                    : functionPackage.desc}
                </p>
                {isLegacy && functionPackage.id === 'PKG-6-1' && <div>Everything Humphree has to offer: </div>}
                {!isLegacy && functionPackage.id === 'PKG-6-1' && <p className='pb-5'>Everything from CRUISE plus:</p>}
                {isLegacy &&
                  functionPackage?.complementaryFeatures.map((feature: string) => {
                    return (
                      <span key={feature} className='flex items-center gap-12'>
                        <FontAwesomeIcon icon={faCheck} />
                        <p className='prose-paragraphBig'>{feature}</p>
                      </span>
                    );
                  })}
                {functionPackage.features.map((feature: string) => {
                  return (
                    <span key={feature} className='flex items-center gap-12'>
                      <FontAwesomeIcon icon={faCheck} />
                      <p className='prose-paragraphBig'>{feature}</p>
                    </span>
                  );
                })}
                {functionPackage.id === 'PKG-6-1' && (
                  <span className='flex items-center gap-12'>
                    <FontAwesomeIcon icon={faPlus} />
                    <p className='prose-paragraphBig'>All future updates</p>
                  </span>
                )}
                <div className='mt-auto grid place-content-end text-right'>
                  <h5 className='prose-heading5'>{handleCustomerDisplayPrice(functionPackage)}</h5>
                  <p className='prose-buttonTiny text-gray-60 mb-1'>{handleRetailDisplayPrice(functionPackage)}</p>
                  <PackageSelectionButton
                    functionPackage={functionPackage}
                    system={system}
                    newPackage={newPackage}
                    setNewPackage={setNewPackage}
                  />
                </div>
              </div>
            );
          })}
      </div>
      <div className='prose-labelPill text-gray-60 flex ml-auto'>All prices are excluding VAT</div>
      <a
        href='https://humphree.com/solutions/lightning-by-humphree/functions'
        target='_blank'
        rel='noreferrer'
        className='prose-paragraphBig  text-primary-100 underline flex justify-center'
      >
        Read more about of functions
      </a>
    </div>
  );
};

export default FunctionPackagesNew;

function PackageSelectionButton({ functionPackage, system, newPackage, setNewPackage }: any) {
  const packages = useSelector((state: any) => state.configurations.packages);
  const upgrades: PackageUpgrade[] = useSelector((state: any) => state.configurations.upgrades);
  const currentPackage: Package = packages.find((pack: Package) => pack.id === system?.packageId);
  const upgradeId = upgrades.find(
    (p: PackageUpgrade) =>
      p.upgradeBasePackageId === functionPackage.id && p?.id && currentPackage?.availableUpgrades.includes(p.id)
  )?.id;

  function handlePackageSelection(functionPackage: FunctionPackage) {
    if (functionPackage.id === system?.packageId) {
      return 'Current license';
    } else if (upgradeId === newPackage || functionPackage.id === newPackage) {
      return 'Selected';
    } else {
      return 'Select package';
    }
  }

  return (
    <div>
      {handlePackageSelection(functionPackage) === 'Current license' && (
        <button
          disabled
          className='h-10 px-5 prose-paragraphBase flex justify-center items-center gap-2 font-bold bg-gray-10 text-white'
        >
          <FontAwesomeIcon icon={faCheck} /> Current license
        </button>
      )}
      {handlePackageSelection(functionPackage) === 'Selected' && (
        <button
          className='h-10 px-5 prose-paragraphBase flex justify-center items-center gap-2 cursor-pointer font-bold bg-primary-200 hover:bg-opacity-80 text-white'
          onClick={() => setNewPackage('')}
        >
          <FontAwesomeIcon icon={faCheck} /> Selected
        </button>
      )}
      {handlePackageSelection(functionPackage) === 'Select package' && (
        <button
          className='h-10 px-5 prose-paragraphBase flex justify-center items-center gap-2 cursor-pointer font-bold bg-primary-100 hover:bg-opacity-80 text-white'
          onClick={() => {
            if (upgradeId) setNewPackage(upgradeId);
            else setNewPackage(functionPackage.id);
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> Select package
        </button>
      )}
    </div>
  );
}
